import { useAuthCheckAccess, useAuthCheckRealmRole } from '@aibees/shared/auth';
import { ComingSoonTag } from '@aibees/shared/components/coming-soon-tag';
import { RouterLink } from '@aibees/shared/router';
import { Box, Link, ListItemText, Stack, Tooltip } from '@mui/material';
import { Icon } from '../../icon';
import type { NavConfigProps, NavItemProps } from '../types';
import { StyledIcon, StyledItem } from './styles';

type Props = NavItemProps & {
  config: NavConfigProps;
};

export default function NavItem({
  item,
  open,
  depth,
  active,
  config,
  hasChild,
  externalLink,
  ...other
}: Props) {
  const {
    title,
    path,
    icon,
    info,
    children,
    disabled,
    caption,
    roles,
    comingSoon,
    realmRoles,
    textSx,
    hidden,
  } = item;

  const subItem = depth !== 1;
  const allowedByRole = useAuthCheckAccess(roles, true);
  const allowedByRealm = useAuthCheckRealmRole(realmRoles, true);
  if (!allowedByRole || !allowedByRealm) return null;
  if (hidden) return null;

  const renderContent = (
    <StyledItem
      disableGutters
      disabled={disabled || comingSoon}
      active={active}
      hasChild={hasChild}
      depth={depth}
      config={config}
      {...other}
    >
      <Stack direction="row" alignItems="center">
        <>
          {icon ? (
            <StyledIcon
              disabled={disabled || comingSoon}
              active={active}
              size={config.iconSize}
            >
              {icon}
            </StyledIcon>
          ) : (
            <StyledIcon
              disabled={disabled || comingSoon}
              active={active}
              size={config.iconSize}
            ></StyledIcon>
          )}
        </>

        {!(config.hiddenLabel && !subItem) && (
          <ListItemText
            primary={title}
            secondary={
              caption ? (
                <Tooltip title={caption} placement="top-start">
                  <span>{caption}</span>
                </Tooltip>
              ) : null
            }
            primaryTypographyProps={{
              noWrap: true,
              typography: 'body3',
              textTransform: 'capitalize',
              fontWeight: active ? 'fontWeightMedium' : 'fontWeightMedium',
            }}
            secondaryTypographyProps={{
              noWrap: true,
              component: 'span',
              typography: 'caption',
              color: 'text.disabled',
            }}
            sx={textSx}
          />
        )}
      </Stack>

      {info && (
        <Box component="span" sx={{ ml: 1, lineHeight: 0 }}>
          {info}
        </Box>
      )}

      {externalLink && !comingSoon && (
        <Icon
          name={'open-in-new-outline'}
          size={20}
          sxx={{ ml: 1, flexShrink: 0, color: 'grey.400' }}
        />
      )}

      {comingSoon && <ComingSoonTag />}

      {!!children?.length && (
        <Icon
          name={open ? 'arrow-accordion-up' : 'arrow-accordion'}
          size={16}
          sxx={{ ml: 1, flexShrink: 0 }}
        />
      )}
    </StyledItem>
  );

  // External link
  if (externalLink)
    return (
      <Link
        href={path}
        target="_blank"
        rel="noopener"
        underline="none"
        color="inherit"
        sx={{
          ...(disabled && {
            cursor: 'default',
          }),
        }}
      >
        {renderContent}
      </Link>
    );

  if (!!children?.length || comingSoon) {
    return renderContent;
  }

  // Default
  return (
    <Link
      component={RouterLink}
      to={path}
      underline="none"
      color="inherit"
      sx={{
        ...(disabled && {
          cursor: 'default',
        }),
      }}
    >
      {renderContent}
    </Link>
  );
}
